import { useEffect, useState } from "react";
import { Plus, Loader2 } from "lucide-react";

import WorkspaceCard from "./WorkspaceCard";
import WorkspaceDrawer from "../drawer/WorkspaceDrawer";
import CreateWorkspaceDrawer from "../create/CreateWorkspaceDrawer";

import {
  getWorkspaces,
} from "../services/workspaceService";


const Workspaces = ({ project }) => {
  const [workspaceData, setWorkspaceData] =
    useState([]);

  const [selectedWorkspace, setSelectedWorkspace] =
    useState(null);

  const [openDrawer, setOpenDrawer] =
    useState(false);

  const [openCreateDrawer, setOpenCreateDrawer] =
    useState(false);

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");


  /**
   * ==========================================================
   * GET PROJECT UUID
   * ==========================================================
   */
  const projectUuid =
    project?.uuid ||
    project?.project_uuid ||
    project?.projectUuid;


  /**
   * ==========================================================
   * NORMALIZE BACKEND WORKSPACE
   *
   * Backend:
   * workspace_name
   * workspace_code
   * workspace_lead_employee_uuid
   *
   * UI:
   * name
   * owner
   * members
   * progress
   * items
   * ==========================================================
   */
  const normalizeWorkspace = (workspace) => {
    return {
      ...workspace,

      // Basic workspace information
      id:
        workspace.uuid ||
        workspace.workspace_uuid ||
        workspace.id ||
        workspace.workspace_code,

      name:
        workspace.workspace_name ||
        workspace.name ||
        "Unnamed Workspace",

      code:
        workspace.workspace_code ||
        workspace.code ||
        "",

      description:
        workspace.description ||
        "",

      status:
        workspace.status ||
        "Planning",

      executionMode:
        workspace.execution_mode ||
        "",

      estimatedHours:
        workspace.estimated_hours || 0,

      displayOrder:
        workspace.display_order || 0,

      // Lead employee UUID
      workspaceLeadEmployeeUuid:
        workspace.workspace_lead_employee_uuid ||
        "",

      // These may not be returned by create/get API yet
      // Keep defaults so existing UI doesn't break.
      owner:
        workspace.workspace_lead_employee_name ||
        workspace.owner ||
        "Not assigned",

      members:
        workspace.member_count ||
        workspace.members_count ||
        workspace.members ||
        0,

      items:
        workspace.item_count ||
        workspace.items_count ||
        workspace.items ||
        0,

      progress:
        workspace.progress ||
        0,
    };
  };


  /**
   * ==========================================================
   * FETCH WORKSPACES
   * ==========================================================
   */
  const fetchWorkspaces = async () => {
    if (!projectUuid) {
      console.warn(
        "Project UUID not available"
      );

      return;
    }

    try {
      setLoading(true);
      setError("");

      const response =
        await getWorkspaces(projectUuid);

      console.log(
        "WORKSPACES API DATA:",
        response
      );


      /**
       * Depending on your backend response:
       *
       * {
       *   success: true,
       *   data: [...]
       * }
       *
       * OR
       *
       * {
       *   data: [...]
       * }
       *
       * OR
       *
       * [...]
       */
      const workspaces =
        Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.data?.data)
          ? response.data.data
          : [];


      const normalizedWorkspaces =
        workspaces.map(
          normalizeWorkspace
        );


      setWorkspaceData(
        normalizedWorkspaces
      );

    } catch (error) {
      console.error(
        "FETCH WORKSPACES ERROR:",
        error
      );

      setError(
        error.response?.data?.message ||
        "Failed to load workspaces."
      );

      setWorkspaceData([]);

    } finally {
      setLoading(false);
    }
  };


  /**
   * ==========================================================
   * LOAD WORKSPACES WHEN PROJECT CHANGES
   * ==========================================================
   */
  useEffect(() => {
    fetchWorkspaces();
  }, [projectUuid]);


  /**
   * ==========================================================
   * OPEN WORKSPACE
   * ==========================================================
   */
  const handleWorkspaceClick = (
    workspace
  ) => {
    setSelectedWorkspace(workspace);
    setOpenDrawer(true);
  };


  /**
   * ==========================================================
   * AFTER CREATE
   * ==========================================================
   */
  const handleWorkspaceCreated = async (newWorkspace) => {
  console.log("NEW WORKSPACE CREATED:", newWorkspace);

  // Close create drawer
  setOpenCreateDrawer(false);

  // Fetch latest workspace data from backend
  await fetchWorkspaces();
};


  return (
    <div className="space-y-6">

      {/* =====================================================
          HEADER
      ===================================================== */}

      <div className="flex items-center justify-between">

        <div>
          <h2 className="text-xl font-bold text-slate-800">
            Workspaces
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            Workspaces available in{" "}
            {project?.projectName ||
              "this project"}
          </p>
        </div>


        {/* CREATE WORKSPACE */}

        <button
          type="button"
          onClick={() =>
            setOpenCreateDrawer(true)
          }
          className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-blue-700"
        >
          <Plus size={17} />

          Create Workspace
        </button>

      </div>


      {/* =====================================================
          ERROR
      ===================================================== */}

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}


      {/* =====================================================
          LOADING
      ===================================================== */}

      {loading && (
        <div className="flex items-center justify-center py-12">

          <div className="flex items-center gap-2 text-sm text-slate-500">

            <Loader2
              size={20}
              className="animate-spin"
            />

            Loading workspaces...

          </div>

        </div>
      )}


      {/* =====================================================
          EMPTY STATE
      ===================================================== */}

      {!loading &&
        workspaceData.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-10 text-center">

            <h3 className="text-lg font-semibold text-slate-700">
              No workspaces found
            </h3>

            <p className="mt-1 text-sm text-slate-500">
              Create a workspace to get started.
            </p>

          </div>
        )}


      {/* =====================================================
          WORKSPACE CARDS
      ===================================================== */}

      {!loading &&
        workspaceData.length > 0 && (

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">

            {workspaceData.map(
              (workspace) => (

                <WorkspaceCard
                  key={
                    workspace.id ||
                    workspace.workspace_code
                  }
                  workspace={workspace}
                  onClick={() =>
                    handleWorkspaceClick(
                      workspace
                    )
                  }
                />

              )
            )}

          </div>

        )}


      {/* =====================================================
          WORKSPACE DETAILS DRAWER
      ===================================================== */}

      <WorkspaceDrawer
        open={openDrawer}
        workspace={selectedWorkspace}
        project={project}
        onClose={() =>
          setOpenDrawer(false)
        }
      />


      {/* =====================================================
          CREATE WORKSPACE DRAWER
      ===================================================== */}

      <CreateWorkspaceDrawer
  open={openCreateDrawer}
  project={project}
  onClose={() =>
    setOpenCreateDrawer(false)
  }
  onWorkspaceCreated={
    handleWorkspaceCreated
  }
/>

    </div>
  );
};


export default Workspaces;